import { useEffect, useRef, useState } from "react";

// Smoothly tweens between numeric values (exchange-style live ticking).
// Optionally flashes a subtle background on increase/decrease.
export const AnimatedNumber = ({
  value = 0,
  decimals = 2,
  prefix = "",
  suffix = "",
  showSign = false,
  className = "",
  flash = false,
  duration = 600,
  "data-testid": testId,
}) => {
  const [display, setDisplay] = useState(value);
  const [dir, setDir] = useState(null); // 'up' | 'down' | null
  const prevRef = useRef(value);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = prevRef.current;
    const to = value;
    if (from === to) return;
    if (flash) setDir(to > from ? "up" : "down");

    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setDisplay(from + (to - from) * eased);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        setDisplay(to);
        prevRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration, flash]);

  useEffect(() => {
    if (!dir) return;
    const id = setTimeout(() => setDir(null), 700);
    return () => clearTimeout(id);
  }, [dir]);

  const sign = showSign ? (display >= 0 ? "+" : "-") : (display < 0 ? "-" : "");
  const absStr = Math.abs(display).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  const flashClass = dir === "up" ? "num-flash-up" : dir === "down" ? "num-flash-down" : "";

  return (
    <span className={`${className} ${flashClass}`} data-testid={testId}>
      {sign}{prefix}{absStr}{suffix}
    </span>
  );
};

export default AnimatedNumber;
